import { useState, useEffect, useLayoutEffect } from 'react';
import {
  getGroupById,
  getGroupedProducts,
  getCatalogSubtypes,
  parseV2SystemMedia,
} from '../../api';
import type { ProductGroup } from '../../types';
import { dedupeSiblingGroups } from '../../lib/dedupeSiblingGroups';
import { filterVitrineSiblingGroups } from '../../lib/vitrineCatalogFilter';
import {
  PIM_BRANCH_UI,
  filterPimSiblings,
  pimBranchKeyFromSlug,
  sortCatalogSiblings,
} from '../../lib/pimBranchUi';

interface SiblingGroup {
  group_id: string;
  name: string;
  subtype_tags?: string[];
  min_price?: number;
  variants_count?: number;
  tile_hint?: string | null;
  protocols?: string[];
  subtype_sort_order?: number;
  branch_sort_order?: number;
}

interface UseProductGroupDataResult {
  group: ProductGroup | null;
  loading: boolean;
  siblingGroups: SiblingGroup[];
  catalogSubtypes: Array<{ subtype: string; slug: string; min_price?: number }>;
  heroReady: boolean;
  setGroup: React.Dispatch<React.SetStateAction<ProductGroup | null>>;
  setHeroReady: React.Dispatch<React.SetStateAction<boolean>>;
  resetSelections: () => void;
}

interface ResetCallback {
  (): void;
}

export function useProductGroupData(
  routeGroupId: string | null,
  onReset: ResetCallback,
  catalogTier?: string | null,
): UseProductGroupDataResult {
  const [group, setGroup] = useState<ProductGroup | null>(null);
  const [loading, setLoading] = useState(true);
  const [siblingGroups, setSiblingGroups] = useState<SiblingGroup[]>([]);
  const [catalogSubtypes, setCatalogSubtypes] = useState<Array<{ subtype: string; slug: string; min_price?: number }>>([]);
  const [heroReady, setHeroReady] = useState(false);

  const resetSelections = onReset;

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    });
  }, [routeGroupId, catalogTier]);

  useEffect(() => {
    if (!routeGroupId) return;
    resetSelections();
    setHeroReady(false);
    getGroupById(routeGroupId, { tier: catalogTier })
      .then((g: ProductGroup) => {
        const gallery = parseV2SystemMedia((g as any)?.gallery);
        setGroup({
          ...g,
          gallery: gallery.length ? gallery : [],
          protocols: Array.isArray((g as any)?.protocols) ? (g as any).protocols : [],
          interface_types: Array.isArray((g as any)?.interface_types) ? (g as any).interface_types : [],
          variants: Array.isArray((g as any)?.variants) ? (g as any).variants : [],
          advantages: Array.isArray((g as any)?.advantages) ? (g as any).advantages : [],
          control_types: Array.isArray((g as any)?.control_types) ? (g as any).control_types : [],
          subtype_tags: Array.isArray((g as any)?.subtype_tags) ? (g as any).subtype_tags : [],
          selector_schema: (g as any)?.selector_schema || {},
          active_tier_slug: (g as any)?.active_tier_slug ?? null,
          available_tier_slugs: Array.isArray((g as any)?.available_tier_slugs)
            ? (g as any).available_tier_slugs
            : [],
        });
        requestAnimationFrame(() => setHeroReady(true));
      })
      .catch((err) => {
        console.error('[useProductGroupData] getGroupById failed', routeGroupId, catalogTier, err);
        setGroup(null);
      })
      .finally(() => setLoading(false));
  }, [routeGroupId, catalogTier, resetSelections]);

  useEffect(() => {
    if (!group?.category && !group?.branch_slug) {
      setSiblingGroups([]);
      return;
    }
    const branchFilter =
      group.branch_slug ||
      (group.group_id?.includes('::') ? group.group_id.split('::')[0] : '') ||
      group.category;
    getGroupedProducts({ category: branchFilter })
      .then((rows) => {
        const normalized = Array.isArray(rows) ? rows : [];
        const compact = normalized
          .map((g: any) => ({
            group_id: String(g?.group_id || ''),
            name: String(g?.name || ''),
            subtype_tags: Array.isArray(g?.subtype_tags) ? g.subtype_tags : [],
            min_price: Number.isFinite(Number(g?.min_price)) ? Number(g?.min_price) : undefined,
            variants_count: Number(g?.variants_count ?? 0),
            tile_hint: typeof g?.tile_hint === 'string' ? g.tile_hint : null,
            protocols: Array.isArray(g?.protocols) ? g.protocols.map((p: unknown) => String(p)) : [],
            subtype_sort_order:
              g?.subtype_sort_order != null && g.subtype_sort_order !== ''
                ? Number(g.subtype_sort_order)
                : undefined,
            branch_sort_order:
              g?.branch_sort_order != null && g.branch_sort_order !== ''
                ? Number(g.branch_sort_order)
                : undefined,
          }))
          .filter((g: SiblingGroup) => g.group_id && g.name);
        let siblings = filterVitrineSiblingGroups(dedupeSiblingGroups(compact));
        const branchSlug = String(branchFilter || '').trim().toLowerCase();
        const pimKey = pimBranchKeyFromSlug(branchSlug);
        if (pimKey) {
          const cfg = PIM_BRANCH_UI[pimKey];
          const canonicalBranch =
            cfg.branchSlugs.find((s) => branchSlug === s || branchSlug.includes(s)) || branchSlug;
          siblings = filterPimSiblings(canonicalBranch, siblings, cfg) as typeof siblings;
        }
        setSiblingGroups(sortCatalogSiblings(siblings).slice(0, 16));
      })
      .catch(() => setSiblingGroups([]));
  }, [group?.category, group?.branch_slug, group?.group_id]);

  useEffect(() => {
    if (!group?.category) { setCatalogSubtypes([]); return; }
    getCatalogSubtypes(group.category).then(setCatalogSubtypes).catch(() => setCatalogSubtypes([]));
  }, [group?.category]);

  return {
    group,
    loading,
    siblingGroups,
    catalogSubtypes,
    heroReady,
    setGroup,
    setHeroReady,
    resetSelections,
  };
}
